import { IntegrationsMenu } from "../../features/integration-discovery/integrations-menu";
import { useRef, useState } from "react";
import styled, { createGlobalStyle, css } from "styled-components";
import { colors } from "../../styles/color-tokens";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Link as LinkComponent } from "react-router-dom";
import { PlatformIcon } from "@dialogueconsulting/sked-ui";
import { HeaderUserMenu } from "./HeaderUserMenu";
import { HeaderSupportMenu } from "./HeaderSupportMenu";
import { AddNewMenu } from "./AddNewMenu";
import { useTrackEvent } from "../../libs/analytics";
import { HEADER_ADD_ACCOUNT_CLICK } from "../../constants/Events";
import { Flex } from "../commonStyled/layout";
import config from "../../config";
import { getImpersonation } from "../../api/impersonation";

// Page title icons removed per Figma design
import { User } from "../../features/user";
import { useSelector } from "react-redux";
import { getUser } from "../../selectors/commonSelectors";
import {
  KnockFeedProvider,
  KnockProvider,
  NotificationFeedPopover,
  UnseenBadge,
} from "@knocklabs/react";
import { cn } from "../../lib/utils";
import { topNavGhostIconButtonClassName } from "./top-nav-button";
import { resolveHeaderHeight, type HeaderVariant } from "./header-height";
interface HeaderProps {
  className?: string;
  variant?: HeaderVariant;
  pageTitle?: string;
  subTitle?: string;
  customLinkInTitle?: "report_inbox_bug";
  icon?: JSX.Element;

  logout(): void;
  openSocialAccountConnectDialog(): void;
}

// Support staff opening the feed must not clear the customer's unseen count.
function suppressMarkAsSeen() {}

function HeaderComponent(props: HeaderProps) {
  const trackEvent = useTrackEvent();
  const user: User = useSelector(getUser);
  const [isVisible, setIsVisible] = useState(false);
  const notifButtonRef = useRef<HTMLButtonElement | null>(null);

  const openSocialAccountConnectDialogWithEvent = (): void => {
    trackEvent({
      eventName: HEADER_ADD_ACCOUNT_CLICK,
      useServices: ["AMP"],
    });
    props.openSocialAccountConnectDialog();
  };

  const titleProps = getRenderIconProps(props.pageTitle);
  const isCalendarUplift = props.variant === "calendar-uplift";
  const isPaidSocial = props.variant === "paid-social";
  const usesFigmaTopNav = isCalendarUplift || isPaidSocial;
  const hasKnockNotifications = Boolean(
    config.knockApiKey &&
    user.preferences?.entitlements?.["knock-notifications"] === "true" &&
    user.knockUserToken
  );

  return (
    <HeaderContainer
      className={props.className}
      $variant={props.variant ?? "default"}
    >
      <HeaderHeightDefinition variant={props.variant ?? "default"} />

      <Title
        data-testid="header-title"
        $variant={props.variant ?? "default"}
        {...titleProps}
      >
        {props.pageTitle && renderIcon(props.pageTitle)}
        <span>{props.pageTitle}</span>
        {props.subTitle && (
          <>
            <TitleDivider />
            <SubTitle $paidSocial={isPaidSocial}>{props.subTitle}</SubTitle>
          </>
        )}
      </Title>

      <Flex className={cn("items-center", usesFigmaTopNav ? "gap-2" : "gap-1")}>
        <AddNewMenu
          figmaTopNav={usesFigmaTopNav}
          openSocialAccountConnectDialog={
            openSocialAccountConnectDialogWithEvent
          }
        />
        <IntegrationsMenu />
        {hasKnockNotifications ? (
          <KnockProvider
            apiKey={config.knockApiKey!}
            user={{ id: user.userId }}
            userToken={user.knockUserToken!}
          >
            <KnockFeedProvider
              feedId="5ea5bd56-4264-4226-8d75-cf83c80cccd5"
              defaultFeedOptions={{ tenant: user.knockTenantId }}
            >
              <>
                <button
                  type="button"
                  ref={notifButtonRef}
                  className={cn(
                    topNavGhostIconButtonClassName,
                    usesFigmaTopNav && "text-[#333333]",
                    isVisible && "bg-blue-50 text-blue-500 hover:bg-blue-50"
                  )}
                  onClick={() => setIsVisible((visible) => !visible)}
                  aria-label="Open notification feed"
                  aria-expanded={isVisible}
                  data-testid="notification-menu-trigger"
                >
                  <i className="fa-regular fa-bell text-base" />
                  <UnseenBadge />
                </button>
                <NotificationFeedPopover
                  buttonRef={notifButtonRef}
                  isVisible={isVisible}
                  onClose={() => setIsVisible(false)}
                  {...(getImpersonation()
                    ? { onOpen: suppressMarkAsSeen }
                    : undefined)}
                />
              </>
            </KnockFeedProvider>
          </KnockProvider>
        ) : usesFigmaTopNav ? (
          <button
            type="button"
            className={cn(topNavGhostIconButtonClassName, "text-[#333333]")}
            aria-label="Notifications unavailable"
            disabled
          >
            <i className="fa-regular fa-bell text-base" />
          </button>
        ) : null}
        <HeaderSupportMenu figmaTopNav={usesFigmaTopNav} />
        <HeaderUserMenu figmaTopNav={usesFigmaTopNav} />
      </Flex>
    </HeaderContainer>
  );
}

const HeaderHeightDefinition = createGlobalStyle<{ variant: HeaderVariant }>`
  body {
    --header-height: ${(props) =>
      resolveHeaderHeight({
        variant: props.variant,
        wide: false,
      })};
    @media (min-width: 1980px) {
      --header-height: ${(props) =>
        resolveHeaderHeight({
          variant: props.variant,
          wide: true,
        })};
    }
  }
`;

function renderIcon(_title: string) {
  // No icons for page titles per Figma design
  return null;
}

function getRenderIconProps(title?: string) {
  switch (title) {
    case "Workshop": {
      return { $fill: false };
    }
    case "Labels": {
      return { $fill: true, $stroke: "none" };
    }
    default: {
      return { $fill: true };
    }
  }
}

const ANIMATION_MS = 500;

export const DropdownMenuContent = styled(DropdownMenu.Content)<{
  padding?: string;
  minWidth?: number;
}>`
  background: ${colors.white};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  padding: ${({ padding = "14px" }): string => padding};
  ${({ minWidth }) =>
    minWidth
      ? css`
          min-width: ${minWidth}px;
        `
      : ""};

  :focus-visible {
    outline: unset;
  }
`;
export const Header = styled(HeaderComponent)``;

export const DropdownButton = styled.button`
  width: 100%;
  background: ${colors.primary900};
  border-radius: 5px;
  color: ${colors.white};
  border: none;
  position: relative;
  font-size: 12px;
  font-weight: 500;
  padding: 0 20px;
  border-radius: 5px;
  outline: none;
  display: inline-block;
  min-width: 0;
  height: 38px;
  line-height: 38px;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  margin-top: 12px;

  &:hover {
    background-color: ${colors.primary700};
    color: ${colors.white};
  }

  &:focus {
    color: #ffffff;
    outline: none;
  }
`;

export const DropdownBoldText = styled.span`
  font-weight: 700;
`;

export const DropdownText = styled.div`
  font-size: 14px;
  line-height: 17px;
  color: ${colors.primary900};
  max-width: 350px;
`;

export const Trigger = styled.div<{ fontWeight?: number }>`
  cursor: pointer;
  font-weight: ${({ fontWeight = 300 }) => fontWeight};
  font-size: 14px;
  line-height: 17px;
  color: #0e1680;
  display: flex;
  align-items: flex-end;
  border-radius: 5px;
  padding: 7px 12px 7px 15px;
  gap: 6px;
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${colors.primary50};
  }

  svg {
    transition: transform 0.3s;
    height: 5px;
    width: 10px;
  }

  &[data-state="open"] {
    background: ${colors.primary100};

    svg {
      transform: rotate(180deg);
    }
  }
`;

const HeaderContainer = styled.div<{ $variant: HeaderVariant }>`
  display: flex;
  height: var(--header-height);
  padding: 0
    ${(props) =>
      props.$variant === "calendar-uplift"
        ? "24px"
        : props.$variant === "paid-social"
          ? "16px"
          : props.$variant === "posts"
            ? "16px"
            : "21px"};
  align-items: center;
  justify-content: space-between;
  background: ${colors.white};
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-bottom: ${(props) =>
    props.$variant === "default" ? `1px solid ${colors.neutral100}` : "0"};
  user-select: none;
  transition: height ${ANIMATION_MS * 1.5}ms;
`;

const Title = styled.div<{
  $variant: HeaderVariant;
  $fill: boolean;
  $stroke?: string;
}>`
  display: flex;
  align-items: center;
  align-content: center;
  font-family: ${(props) =>
    props.$variant === "paid-social"
      ? "'Gabarito', 'Inter', system-ui, sans-serif"
      : props.$variant === "calendar-uplift"
        ? "'Gotham SSm A', 'Gotham SSm B', Helvetica, Arial, sans-serif"
        : "inherit"};
  column-gap: ${(props) => (props.$variant === "paid-social" ? "8px" : "20px")};
  font-weight: ${(props) =>
    props.$variant === "calendar-uplift" || props.$variant === "paid-social"
      ? 700
      : 600};
  font-size: ${(props) =>
    props.$variant === "calendar-uplift" || props.$variant === "paid-social"
      ? "20px"
      : "18px"};
  line-height: ${(props) =>
    props.$variant === "calendar-uplift" || props.$variant === "paid-social"
      ? "24px"
      : "22px"};
  color: ${(props) =>
    props.$variant === "calendar-uplift" || props.$variant === "paid-social"
      ? "#333333"
      : "#1a1823"};

  svg {
    stroke: ${(props) => props.$stroke || "#1A1823"};
    ${(props) => props.$fill && `fill: #1A1823;`}
  }
`;

const TitleDivider = styled.div`
  height: 24px;
  width: 1px;
  background: #0e1680;
`;

const SubTitle = styled.div<{ $paidSocial: boolean }>`
  font-family: ${(props) =>
    props.$paidSocial
      ? "'Gabarito', 'Inter', system-ui, sans-serif"
      : "inherit"};
  font-size: ${(props) => (props.$paidSocial ? "20px" : "16px")};
  line-height: ${(props) => (props.$paidSocial ? "24px" : "inherit")};
  font-weight: 500;
`;

export const Link = styled(LinkComponent)`
  display: block;
  white-space: nowrap;
  color: #0e1680;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  padding: 10px 16px;

  &:first-child {
    margin-top: 0;
  }

  &.active,
  &:hover {
    color: #0e1680;
    text-decoration: none;
  }
`;

export const Logout = styled.div`
  cursor: pointer;
  color: #0e1680;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  padding: 10px 16px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;

  .icon {
    margin-right: 12px;
  }

  &:hover {
    color: #0e1680;
    background: ${colors.primary25};
  }
  &:active {
    background: ${colors.primary50};
  }
`;

export const SubMenu = styled.div`
  ${Link} {
    line-height: 24px;

    .icon {
      margin-right: 10px;
    }
    :nth-child(1) {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
    }
    :hover {
      background: #fcfaff;
    }
    :active {
      background: #edeaff;
    }
  }
`;

export const StyledPlatform = styled(function StyledPlatform({
  type,
  className,
}: {
  type: string;
  size: number;
  className?: string;
}) {
  return <PlatformIcon platform={type} className={className} />;
})<{ size: number }>`
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
`;
